import { Button } from "@/design-system/Button/Button";
import {
  ChemistryDelta,
  ChemistryIcon,
} from "@/design-system/Chemistry/Chemistry";
import { useTokenCosts } from "@/design-system/Evolution/useTokenCosts";
import PlayerCardCondensed from "@/design-system/PlayerCard/PlayerCardCondensed";
import { ContentTag } from "@/design-system/Tags/ContentTag/ContentTag";
import {
  MetaRatingChange,
  MetaRatingChip,
} from "@/design-system/Tags/MetaRatingChip/MetaRatingChip";
import { NewTag } from "@/design-system/Tags/NewTag/NewTag";
import { CoinIcon, MetaRatingIcon, arrow } from "@/designSystemIcons";
import { EmptySlotCard } from "@/features/MyClub/ActiveSquad/EmptySlotCard";
import { cn } from "@/lib/utils";
import { contentLabelsSelector } from "@/redux/loadedContent/loadedContentSlice";
import type {
  Recommendation,
  RecommendationType,
  UpgradeType,
} from "@repo/shared/api";
import { costTokens } from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { type RecFigures, recFigures } from "@repo/shared/myClub";
import { Chemistry } from "@repo/shared/types";
import { Eye, GitCompareArrows } from "lucide-react";
import { Fragment, ReactNode } from "react";
import { useSelector } from "react-redux";

/** What kind of upgrade it is; a player to buy reads as Market. */
const TAG: Record<
  RecommendationType,
  "market" | "evolution" | "sbc" | "club" | "objective"
> = {
  market: "market",
  evolution: "evolution",
  sbc: "sbc",
  club: "club",
  objective: "objective",
};

const CARD_SCALE = 0.4;

type RecPlayer = NonNullable<Recommendation["newPlayer"]>;

/* The chemistry chip on the card's corner, sized to the small card. */
const CHEM_CHIP_SCALE = 0.7;

const Card = ({
  player,
  chemistry,
  previousChemistry,
}: {
  player: RecPlayer;
  /** The player's chemistry in the slot, before or after the swap. */
  chemistry?: number;
  /** On the incoming card: the outgoing chemistry, to show the change. */
  previousChemistry?: number;
}) => (
  <PlayerCardCondensed
    player={player}
    position={player.position}
    scale={CARD_SCALE}
    hidePrice
    chemistry={chemistry as Chemistry | undefined}
    previousChemistry={previousChemistry as Chemistry | undefined}
    chemistryScale={CHEM_CHIP_SCALE}
  />
);

/* A change too small to show once rounded to one decimal. */
const shows = (value: number) => Math.abs(value) >= 0.05;

/** One labelled line in the card's figures column. */
const Figure = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-xxs tracking-wider text-foreground-muted uppercase">
      {label}
    </span>
    <div className="flex items-center gap-1.5 text-sm font-semibold">
      {children}
    </div>
  </div>
);

/**
 * One upgrade for the active squad, small enough to list beside the pitch:
 * outgoing → incoming player, and what the swap does for the player and for
 * the squad. Preview tries it on the pitch; Compare opens both players.
 */
export const RecommendationUpgradeCard = ({
  recommendation,
  figures = recFigures(recommendation),
  previewing,
  stale = false,
  onPreview,
  onCompare,
  onOpenPlayer,
}: {
  recommendation: Recommendation;
  /** What the swap does; defaults to the recommendation's own gains. */
  figures?: RecFigures;
  previewing: boolean;
  /** Made for an older version of the squad: shown greyed out, not usable. */
  stale?: boolean;
  onPreview: () => void;
  onCompare: () => void;
  onOpenPlayer: () => void;
}) => {
  const { t } = useTranslation(["myClub", "common"]);
  const { positions } = useSelector(contentLabelsSelector);
  const { oldPlayer, newPlayer, type, mainContribution, title } =
    recommendation;
  if (!newPlayer) return null;

  const {
    playerMeta,
    squadMeta,
    squadChem,
    playerMetaBefore,
    playerMetaAfter,
    playerChemBefore,
    playerChemAfter,
  } = figures;

  // Older backends only send the English `title`.
  const heading = !mainContribution
    ? title
    : mainContribution === "playerMeta" || mainContribution === "teamMeta"
      ? t("recommendations.card.title.upgrade", {
          position: positions[newPlayer.position]?.name ?? newPlayer.position,
        })
      : t("recommendations.card.title.boostChem");

  const cost =
    type === "evolution"
      ? newPlayer.evolutionCost || null
      : (recommendation.price ?? newPlayer.priceInfo?.displayPrice);
  const tokenCosts = useTokenCosts(
    type === "evolution"
      ? costTokens({ tokens: newPlayer.evolutionTokenCosts })
      : [],
  );

  return (
    <article
      className={cn(
        "flex flex-col gap-3 rounded-xl border bg-neutral-800 p-3 transition-colors",
        previewing ? "border-blue-800/70 bg-blue-950/40" : "border-neutral-700",
        stale && "pointer-events-none opacity-45 grayscale",
      )}
      aria-disabled={stale}
    >
      <header className="flex items-center gap-2">
        <ContentTag contentType={TAG[type]} />
        <h3 className="truncate text-sm font-semibold">{heading}</h3>
        {recommendation.isNew && <NewTag />}
        <span className="ml-auto flex shrink-0 items-center gap-1 text-xs">
          {/* A club player is already the user's: nothing to pay. */}
          {type === "club" ||
          (type === "evolution" && !cost && tokenCosts.length === 0) ? (
            <>
              <CoinIcon size={12} />
              <span className="font-oswald text-sm font-medium text-sbc-coin">
                {t("recs.free")}
              </span>
            </>
          ) : (
            <>
              {cost ? (
                <>
                  <CoinIcon size={12} />
                  <span className="font-oswald text-sm font-medium text-neutral-300 tabular-nums">
                    {cost.toLocaleString("en-US")}
                  </span>
                </>
              ) : null}
              {tokenCosts.map((token) => (
                <Fragment key={token.eventTokenId}>
                  <img
                    className="ml-1 size-3.5 object-contain"
                    src={token.iconUrl}
                    alt={token.name}
                  />
                  <span className="font-oswald text-sm font-medium text-neutral-300 tabular-nums">
                    {token.amount.toLocaleString("en-US")}
                  </span>
                </Fragment>
              ))}
            </>
          )}
        </span>
      </header>

      <div className="flex items-center gap-3">
        <button
          onClick={onOpenPlayer}
          aria-label={newPlayer.cardName}
          className="flex shrink-0 items-center gap-0.5 rounded-lg"
        >
          {/* An empty position: nobody goes out. */}
          {oldPlayer?.resourceId ? (
            <Card player={oldPlayer} chemistry={playerChemBefore} />
          ) : (
            oldPlayer && <EmptySlotCard scale={CARD_SCALE} />
          )}
          <span className="size-2.5 -rotate-90 text-dark-tertiary">
            {arrow}
          </span>
          <Card
            player={newPlayer}
            chemistry={playerChemAfter}
            previousChemistry={playerChemBefore}
          />
        </button>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <Figure label={t("recs.player")}>
            {playerMetaAfter != null ? (
              <MetaRatingChip
                metaRating={playerMetaAfter}
                metaRatingDelta={
                  playerMetaBefore != null
                    ? playerMetaAfter - playerMetaBefore
                    : undefined
                }
              />
            ) : shows(playerMeta) ? (
              <>
                <MetaRatingIcon width="14" height="14" />
                <MetaRatingChange value={playerMeta} />
              </>
            ) : null}
          </Figure>
          {/* Losses too: a swap can cost the players around it chemistry. */}
          {(shows(squadMeta) || squadChem !== 0) && (
            <Figure label={t("recs.squad")}>
              {shows(squadMeta) && (
                <>
                  <MetaRatingIcon width="14" height="14" />
                  <MetaRatingChange value={squadMeta} />
                </>
              )}
              {squadChem !== 0 && (
                <>
                  <ChemistryIcon chemistry={3} className="h-3 w-3.5" />
                  <ChemistryDelta value={squadChem} />
                </>
              )}
            </Figure>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Button
          variant={previewing ? "primary" : "secondary"}
          size="small"
          stretch
          icon={<Eye />}
          onClick={onPreview}
          disabled={!oldPlayer || stale}
        >
          {previewing ? t("recs.previewing") : t("recs.preview")}
        </Button>
        <Button
          variant="secondary"
          size="small"
          stretch
          icon={<GitCompareArrows />}
          onClick={onCompare}
          // Nobody to compare with in an empty position.
          disabled={!oldPlayer?.resourceId || stale}
        >
          {t("recs.compare")}
        </Button>
      </div>
    </article>
  );
};
